/* @flow */
import { graphql } from 'react-apollo';
import invariant from 'invariant';
import * as React from 'react';

import { mountModal } from 'lib/modal';
import ADD_DEVICE_TO_USER_MUTATION from 'users/queries/AddDeviceToUserMutation.gql';
import assertions from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CONNECT_USER_ADDING_NUMBER_QUERY from 'users/queries/ConnectUserAddingNumberQuery.gql';
import CONTACTABILITY_EDUCATION_QUERY from 'chat/queries/contactabilityEducationQuery.gql';
import Dialog from 'components/Dialog';
import FormElement from 'components/FormElement';
import Link from 'components/Link';
import PhoneCallModal from 'phoneCalls/components/PhoneCallModal';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
import RemindAPI from 'lib/RemindAPI';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import type { ConnectUserAddingNumber_me_connectedUsers } from 'users/queries/__generated__/ConnectUserAddingNumber';

import './styles/UsersAddNumberToUserModal.scss';

type Props = {
  onRequestHide: () => void,
  addDeviceToUser: (userId: Backbone$Id, address: string) => Promise<*>,
  userName: string,
  subtext: string,
  submitLabel: string,
  userId: Backbone$Id,
  userUuid: string,
  startCall?: Function,
  onCancel: Function,
};

type State = {
  errorMessage: ?string,
  existingUser: ?ConnectUserAddingNumber_me_connectedUsers,
  submitting: boolean,
  phoneNumber: string,
};

class UsersAddNumberToUserModal extends React.Component<Props, State> {
  state: State = {
    errorMessage: null,
    existingUser: null,
    submitting: false,
    phoneNumber: '',
  };

  callExistingUser = () => {
    const { existingUser } = this.state;
    invariant(existingUser, 'callExistingUser called with no existing user');
    mountModal(<PhoneCallModal calleeUuid={existingUser.uuid} />, this);
    this.props.onRequestHide();
  };

  submitForm = () => {
    const { userId, userUuid, addDeviceToUser, onRequestHide, startCall } = this.props;
    const { phoneNumber } = this.state;
    this.setState({ submitting: true, errorMessage: null, existingUser: null });
    addDeviceToUser(userId, 'sms://' + phoneNumber)
      .then(
        ({ data }) => {
          const { error } = data.addDeviceToUser;
          if (error) {
            if (error.code === 'already_taken_connected') {
              RemindAPI.execute({
                query: CONNECT_USER_ADDING_NUMBER_QUERY,
                variables: { smsAddresses: [phoneNumber] },
              }).then(
                res => {
                  const users = res.me.connectedUsers;
                  this.setState({
                    existingUser: users[0],
                    errorMessage: error.message,
                    submitting: false,
                  });
                },
                () => {
                  this.setState({ errorMessage: error.message, submitting: false });
                  ToastActions.showErrorToast(error.message);
                }
              );
            } else {
              const message =
                error.code === 'validation_error' ? error.fieldErrors[0].message : error.message;
              ToastActions.showErrorToast(message);
              this.setState({ submitting: false, errorMessage: message });
            }
          } else if (startCall) {
            onRequestHide();
            startCall(userUuid);
            ToastActions.showSuccessToast(`Added a number to ${this.props.userName}`);
          } else {
            onRequestHide();
            ToastActions.showSuccessToast(`Added a number to ${this.props.userName}`);
          }
        },
        () => {
          ToastActions.showErrorToast('Error adding device to user');
          this.setState({ submitting: false });
        }
      )
      .catch(e => {
        console.error('Error after adding device to user', e);
        ToastActions.showErrorToast('Error adding device to user');
        this.props.onRequestHide();
      });
  };

  setStateField = (name: string, value: *) => {
    this.setState({ [name]: value });
  };

  render() {
    const { subtext, submitLabel, userName, startCall } = this.props;
    const { submitting, errorMessage, existingUser, phoneNumber } = this.state;
    return (
      <Dialog
        title={`What is ${userName}'s mobile number?`}
        subtext={subtext}
        size="small"
        onDialogDismiss={this.props.onCancel}
        onRequestHide={this.props.onRequestHide}
        hideCloseButton>
        <BoxBody className="UsersAddNumberToUserModal">
          <FormElement
            label="Phone number"
            labelHidden
            showErrorAfterBlur
            validate={e => {
              assertions.number(e.raw, 'Invalid phone number.');
              assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
            }}
            onValidChange={i => this.setStateField('phoneNumber', i.raw)}
            onInvalidChange={() => this.setStateField('phoneNumber', undefined)}
            element={PhoneNumberMaskedInput}
            elementProps={{
              placeholder: '(555) 555-5555',
              className: errorMessage && 'UsersAddNumberToUserModal-input-error',
            }}
          />
          {existingUser && (
            <Row size="medium">
              <Text align="center" size="xsmall" color="lava">
                {errorMessage}.
                {startCall && (
                  <Link data-test="existing-user" block onClick={this.callExistingUser}>
                    Call {existingUser.name}
                  </Link>
                )}
              </Text>
            </Row>
          )}
          <Button
            onClick={this.submitForm}
            disabled={submitting || !phoneNumber}
            type="submit"
            data-test="submit-button">
            {submitLabel}
          </Button>
        </BoxBody>
      </Dialog>
    );
  }
}

const withMutations = graphql(ADD_DEVICE_TO_USER_MUTATION, {
  props({ mutate, ownProps }) {
    return {
      addDeviceToUser(userId: Backbone$Id, address: string) {
        const input = { userId, address };
        return mutate({
          variables: { input },
          refetchQueries: [
            {
              query: CONTACTABILITY_EDUCATION_QUERY,
              variables: { uuid: ownProps.userUuid },
            },
          ],
        });
      },
    };
  },
});

export { UsersAddNumberToUserModal };

export default withMutations(UsersAddNumberToUserModal);
